/**
 * GA4 + Meta Pixel com Consent Mode v2 — só activa após consentimento.
 * Scripts não são injectados antes do utilizador aceitar.
 */

/** @typedef {{ id: string | number, nome?: string, codigo_erp?: unknown, preco?: unknown }} TrackingProduct */
/** @typedef {{ product_id?: number, id?: number, qty: number, nome?: string, unit?: number }} TrackingLine */

const CURRENCY = 'BRL';

/** @type {string} */
let loadedGa4Id = '';
/** @type {string} */
let loadedPixelId = '';
/** @type {boolean} */
let ga4Ready = false;
/** @type {boolean} */
let pixelReady = false;

/**
 * @param {TrackingProduct | null | undefined} p
 * @param {number} [qty]
 */
export function productToTrackingItem(p, qty = 1) {
  if (!p) return null;
  const id = p.id != null ? String(p.id) : '';
  if (!id) return null;
  const erp = p.codigo_erp != null && String(p.codigo_erp).trim() !== '' ? String(p.codigo_erp).trim() : id;
  const price = Number(p.preco);
  return {
    item_id: erp,
    item_name: String(p.nome || 'Produto'),
    price: Number.isFinite(price) ? price : 0,
    quantity: Math.max(1, Math.floor(Number(qty) || 1)),
  };
}

/**
 * @param {TrackingLine[]} lines
 * @param {(id: string | number) => TrackingProduct | null | undefined} resolveProduct
 */
export function linesToGaItems(lines, resolveProduct) {
  const out = [];
  for (const row of lines) {
    const pid = row.product_id ?? row.id;
    const pr = resolveProduct(pid);
    const item = productToTrackingItem(pr, row.qty);
    if (item) out.push(item);
  }
  return out;
}

/** @param {TrackingLine[]} lines */
export function linesTotalValue(lines) {
  let total = 0;
  for (const row of lines) {
    const unit = Number(row.unit);
    const qty = Number(row.qty);
    if (Number.isFinite(unit) && Number.isFinite(qty)) total += unit * qty;
  }
  return Math.round(total * 100) / 100;
}

/**
 * @param {{ analytics: boolean, marketing: boolean }} consent
 */
function ga4ConsentUpdate(consent) {
  if (typeof window === 'undefined' || typeof window.gtag !== 'function') return;
  const analyticsGranted = consent.analytics ? 'granted' : 'denied';
  const adsGranted = consent.marketing ? 'granted' : 'denied';
  window.gtag('consent', 'update', {
    analytics_storage: analyticsGranted,
    ad_storage: adsGranted,
    ad_user_data: adsGranted,
    ad_personalization: adsGranted,
  });
}

/**
 * @param {string} measurementId
 * @param {{ analytics: boolean, marketing: boolean }} consent
 */
export function initGa4(measurementId, consent) {
  if (typeof window === 'undefined' || !measurementId) return;
  if (loadedGa4Id === measurementId && ga4Ready) {
    ga4ConsentUpdate(consent);
    return;
  }

  window.dataLayer = window.dataLayer || [];
  window.gtag = function gtag() {
    window.dataLayer.push(arguments);
  };

  window.gtag('consent', 'default', {
    analytics_storage: 'denied',
    ad_storage: 'denied',
    ad_user_data: 'denied',
    ad_personalization: 'denied',
    wait_for_update: 500,
  });

  const script = document.createElement('script');
  script.async = true;
  script.src = `https://www.googletagmanager.com/gtag/js?id=${encodeURIComponent(measurementId)}`;
  document.head.appendChild(script);

  window.gtag('js', new Date());
  window.gtag('config', measurementId, { send_page_view: false });

  ga4ConsentUpdate(consent);
  loadedGa4Id = measurementId;
  ga4Ready = true;
}

/**
 * @param {string} pixelId
 */
export function initMetaPixel(pixelId) {
  if (typeof window === 'undefined' || !pixelId) return;
  if (loadedPixelId === pixelId && pixelReady) return;

  const n = window;
  if (typeof n.fbq !== 'function') {
    const fbq = function fbq() {
      if (fbq.callMethod) {
        fbq.callMethod.apply(fbq, arguments);
      } else {
        fbq.queue.push(arguments);
      }
    };
    n.fbq = fbq;
    if (!n._fbq) n._fbq = fbq;
    fbq.push = fbq;
    fbq.loaded = true;
    fbq.version = '2.0';
    fbq.queue = [];
    const script = document.createElement('script');
    script.async = true;
    script.src = 'https://connect.facebook.net/en_US/fbevents.js';
    const first = document.getElementsByTagName('script')[0];
    first.parentNode.insertBefore(script, first);
  }

  window.fbq('init', pixelId);
  pixelReady = true;
  loadedPixelId = pixelId;
}

/** @param {boolean} enabled */
function canGa4(enabled) {
  return enabled && ga4Ready && typeof window.gtag === 'function';
}

/** @param {boolean} enabled */
function canPixel(enabled) {
  return enabled && pixelReady && typeof window.fbq === 'function';
}

/**
 * @param {boolean} analyticsOk
 * @param {{ page_path?: string, page_title?: string }} [params]
 */
export function trackPageView(analyticsOk, params = {}) {
  if (!canGa4(analyticsOk)) return;
  window.gtag('event', 'page_view', {
    page_path: params.page_path || window.location.pathname + window.location.search,
    page_title: params.page_title || document.title,
  });
}

/**
 * @param {boolean} analyticsOk
 * @param {boolean} marketingOk
 * @param {TrackingProduct} product
 */
export function trackViewItem(analyticsOk, marketingOk, product) {
  const item = productToTrackingItem(product, 1);
  if (!item) return;
  const value = item.price;

  if (canGa4(analyticsOk)) {
    window.gtag('event', 'view_item', {
      currency: CURRENCY,
      value,
      items: [item],
    });
  }
  if (canPixel(marketingOk)) {
    window.fbq('track', 'ViewContent', {
      content_ids: [item.item_id],
      content_name: item.item_name,
      content_type: 'product',
      value,
      currency: CURRENCY,
    });
  }
}

/**
 * @param {boolean} analyticsOk
 * @param {boolean} marketingOk
 * @param {TrackingProduct} product
 * @param {number} qty
 */
export function trackAddToCart(analyticsOk, marketingOk, product, qty) {
  const item = productToTrackingItem(product, qty);
  if (!item) return;
  const value = Math.round(item.price * item.quantity * 100) / 100;

  if (canGa4(analyticsOk)) {
    window.gtag('event', 'add_to_cart', {
      currency: CURRENCY,
      value,
      items: [item],
    });
  }
  if (canPixel(marketingOk)) {
    window.fbq('track', 'AddToCart', {
      content_ids: [item.item_id],
      content_name: item.item_name,
      content_type: 'product',
      value,
      currency: CURRENCY,
    });
  }
}

/**
 * @param {boolean} analyticsOk
 * @param {boolean} marketingOk
 * @param {TrackingLine[]} lines
 * @param {(id: string | number) => TrackingProduct | null | undefined} resolveProduct
 */
export function trackBeginCheckout(analyticsOk, marketingOk, lines, resolveProduct) {
  const items = linesToGaItems(lines, resolveProduct);
  if (!items.length) return;
  const value = items.reduce((s, it) => s + it.price * it.quantity, 0);

  if (canGa4(analyticsOk)) {
    window.gtag('event', 'begin_checkout', {
      currency: CURRENCY,
      value: Math.round(value * 100) / 100,
      items,
    });
  }
  if (canPixel(marketingOk)) {
    window.fbq('track', 'InitiateCheckout', {
      content_ids: items.map((it) => it.item_id),
      num_items: items.reduce((s, it) => s + it.quantity, 0),
      value: Math.round(value * 100) / 100,
      currency: CURRENCY,
    });
  }
}

/**
 * @param {boolean} analyticsOk
 * @param {boolean} marketingOk
 * @param {{ orderId: string | number, total: number, items: TrackingLine[] }} order
 * @param {(id: string | number) => TrackingProduct | null | undefined} resolveProduct
 */
export function trackPurchase(analyticsOk, marketingOk, order, resolveProduct) {
  const items = linesToGaItems(order.items, resolveProduct);
  const value = Number.isFinite(order.total) ? order.total : linesTotalValue(order.items);
  const txId = String(order.orderId);

  if (canGa4(analyticsOk)) {
    window.gtag('event', 'purchase', {
      transaction_id: txId,
      currency: CURRENCY,
      value,
      items,
    });
  }
  if (canPixel(marketingOk)) {
    window.fbq('track', 'Purchase', {
      content_ids: items.map((it) => it.item_id),
      value,
      currency: CURRENCY,
    });
  }
}

/** Reinicia estado em memória (testes / troca de loja na mesma SPA). */
export function resetStoreTrackingRuntime() {
  loadedGa4Id = '';
  loadedPixelId = '';
  ga4Ready = false;
  pixelReady = false;
}
